import * as THREE from 'three';
import type {MutableRefObject} from 'react';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import CameraSetup from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';
import productEvents from 'components/customer/RoomPlanner/lib/useProductEvents';
import {isSelectionOutline} from 'components/customer/RoomPlanner/helpers/placeholder/highlight';
import {syncPlaceholderProductEvents} from 'components/customer/RoomPlanner/helpers/placeholder/syncEvents';
import {unregisterPlaceholderId} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {roomPlannerApi} from 'components/customer/RoomPlanner/store/roomPlannerApi';
import {resolveLayoutPlaceholderId} from 'components/customer/RoomPlanner/lib/useAutoSave';
import {store} from 'store/customer/storeSetup';

const disposePlaceholderMesh = (group: CabinetMesh): void => {
    CameraSetup?.scene?.remove(group);
    group.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) return;
        if (isSelectionOutline(child)) return;
        (child.geometry as THREE.BufferGeometry)?.dispose();
        if (child.material instanceof THREE.Material) {
            child.material.dispose();
            return;
        }
        if (Array.isArray(child.material)) {
            (child.material as THREE.Material[]).forEach((material) =>
                material.dispose()
            );
        }
    });
};

/**
 * Remove a placeholder from the scene, list, and (optionally) the layout API.
 * @param {CabinetMesh} group Placeholder to remove
 * @param {MutableRefObject<CabinetMesh[] | undefined>} placeholderListRef Live placeholder list
 * @param {MutableRefObject<CabinetMesh[] | undefined>=} cabinetGroupListRef Cabinets (for product events)
 * @param {boolean=} persist When false, skip the API delete (mesh-only undo helper)
 * @return {void}
 */
export const removePlaceholderFromScene = (
    group: CabinetMesh,
    placeholderListRef: MutableRefObject<CabinetMesh[] | undefined>,
    cabinetGroupListRef?: MutableRefObject<CabinetMesh[] | undefined>,
    persist = true
): void => {
    const placeholderId = String(group.userData?.id ?? '');
    placeholderListRef.current = (placeholderListRef.current ?? []).filter(
        (item) => String(item.userData?.id) !== placeholderId
    );

    disposePlaceholderMesh(group);
    productEvents.clearSelectedIf(group);

    if (CameraSetup?.controls) {
        CameraSetup.controls.enabled = true;
    }
    store.dispatch(unregisterPlaceholderId(placeholderId));

    if (persist) {
        void resolveLayoutPlaceholderId(group).then((layoutPlaceholderId) => {
            if (layoutPlaceholderId != null) {
                void store.dispatch(
                    roomPlannerApi.endpoints.deletePlaceholderPosition.initiate(
                        layoutPlaceholderId
                    )
                );
            }
        });
    }

    syncPlaceholderProductEvents(cabinetGroupListRef, {resetPointer: true});
};

/**
 * Drop every placeholder mesh from the scene and the live list.
 * Does not delete layout records, so a room change can load the next room
 * without removing the previous room's saved placements.
 *
 * @param {MutableRefObject<CabinetMesh[] | undefined> | undefined} placeholderListRef Live placeholder list
 * @param {MutableRefObject<CabinetMesh[] | undefined>=} cabinetGroupListRef Cabinets (for product events)
 * @return {void}
 */
export const clearPlaceholdersFromScene = (
    placeholderListRef: MutableRefObject<CabinetMesh[] | undefined> | undefined,
    cabinetGroupListRef?: MutableRefObject<CabinetMesh[] | undefined>
): void => {
    if (!placeholderListRef?.current?.length) return;
    const existing = [...placeholderListRef.current];
    existing.forEach((group) => {
        removePlaceholderFromScene(
            group,
            placeholderListRef,
            cabinetGroupListRef,
            false
        );
    });
};
